import { useCallback, useEffect, useRef, useState } from 'react';
import GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import Point from '@arcgis/core/geometry/Point';
import Polygon from '@arcgis/core/geometry/Polygon';
import Graphic from '@arcgis/core/Graphic';
import * as webMercatorUtils from '@arcgis/core/geometry/support/webMercatorUtils';
import MapView from '@arcgis/core/views/MapView';
import {
  symbolToPictureMarkerMap,
  symbolToGeometryTypeMap,
  type SymbolGeometryType,
  type UscgSymbol,
} from './uscgSymbology';
import { WGS84_SPATIAL_REFERENCE } from './spatialReference';
import {
  createPolylineFromPoints,
  createPolylineSymbol,
} from './polylineGraphics';
import type { DrawingUpdate, PointGeometry } from './constants';
import { useArcgisSketchLayer } from './useArcgisSketchLayer';
import { v4 as uuidv4 } from 'uuid';
import { useLatest } from 'react-use';
import { colord } from 'colord';
import { arcgisGeometryToGeoJson } from './arcgisGeometryToGeoJson';
import { normalizeOpacity } from './normalizeOpacity';
import type { Geometry } from 'geojson';

type SymbolPoint = PointGeometry & {
  id: string;
  order: number;
};

type SymbolLine = {
  id: string;
  points: SymbolPoint[];
};

const DEFAULT_POLYGON_COLOR_HEX = '#1e40af';

const polygonFillSymbol: __esri.SimpleFillSymbolProperties & {
  type: 'simple-fill';
} = {
  type: 'simple-fill',
  color: [30, 64, 175, 0.25],
  outline: {
    color: [30, 58, 138, 1],
    width: 2,
  },
};

const createPolygonFillSymbol = (
  colorHex?: string,
  opacity?: number
): __esri.SimpleFillSymbolProperties & { type: 'simple-fill' } => {
  if (!colorHex) return polygonFillSymbol;

  const parsedColor = colord(colorHex);
  if (!parsedColor.isValid()) {
    return polygonFillSymbol;
  }

  const { r, g, b } = parsedColor.toRgb();

  return {
    type: 'simple-fill',
    color: [r, g, b, normalizeOpacity(opacity)],
    outline: {
      color: [r, g, b, 1],
      width: 2,
    },
  };
};

/** Map drawing hook for USCG symbols. Supports single point and line symbols. */
export const useUscgSymbolDrawing = ({
  mapView,
}: {
  mapView: MapView | null;
}) => {
  const [geometryType, setGeometryType] = useState<SymbolGeometryType | null>(
    null
  );
  const [points, setPoints] = useState<SymbolPoint[]>([]);
  const [line, setLine] = useState<SymbolLine | null>(null);
  const [polygon, setPolygon] = useState<Geometry | null>(null);
  const [polygonColorHex, setPolygonColorHex] = useState(
    DEFAULT_POLYGON_COLOR_HEX
  );
  const [polygonOpacity, setPolygonOpacity] = useState(normalizeOpacity());

  const pendingLayerRef = useRef<GraphicsLayer>(
    new GraphicsLayer({ title: 'Pending Drawing Layer' })
  );

  const latestGeometryType = useLatest(geometryType);

  const onDrawingUpdate = useCallback(
    (update: DrawingUpdate) => {
      setPoints((prev) => {
        const currentType = latestGeometryType.current;

        if (update.kind === 'polyline') {
          return update.points.map((geometry, index) => ({
            id: uuidv4(),
            longitude: geometry.longitude,
            latitude: geometry.latitude,
            order: index,
          }));
        }

        const newPoint: SymbolPoint = {
          id: uuidv4(),
          longitude: update.point.longitude,
          latitude: update.point.latitude,
          order: prev.length,
        };

        if (currentType === 'single') {
          return [newPoint];
        }

        if (currentType === 'line') {
          return [...prev, newPoint];
        }

        return prev;
      });
    },
    [latestGeometryType]
  );

  const handleCreateGeometry = useCallback(
    async ({
      geometry,
      sketchViewModel,
    }: {
      geometry: __esri.Geometry;
      sketchViewModel: __esri.SketchViewModel;
    }) => {
      if (geometry.type === 'point') {
        const pointGeom = geometry as __esri.Point;
        const longitude = pointGeom.longitude ?? pointGeom.x;
        const latitude = pointGeom.latitude ?? pointGeom.y;

        if (typeof longitude === 'number' && typeof latitude === 'number') {
          onDrawingUpdate({
            kind: 'point',
            point: { longitude, latitude },
          });
        }

        // For multi-point geometries (for example line-style symbols), immediately
        // start a new point draw so the user can keep clicking until drawing stops.
        if (latestGeometryType.current !== 'single') {
          sketchViewModel.create('point');
        }
      } else if (geometry.type === 'polyline') {
        const polyline = geometry as __esri.Polyline;
        const spatialRef = polyline.spatialReference;
        const path = polyline.paths[0] ?? [];

        const nextPoints: PointGeometry[] = path.map(([x, y]) => {
          let longitude = x;
          let latitude = y;

          const wkid = spatialRef?.wkid;
          // If the map is in Web Mercator (102100/3857), convert meter-based
          // map units into geographic lon/lat before reporting to consumers.
          if (wkid === 102100 || wkid === 3857) {
            const [lng, lat] = webMercatorUtils.xyToLngLat(x, y);
            longitude = lng;
            latitude = lat;
          }

          return { longitude, latitude };
        });

        if (nextPoints.length > 0) {
          onDrawingUpdate({
            kind: 'polyline',
            points: nextPoints,
          });
        }
      } else if (geometry.type === 'polygon') {
        const geoJson = await arcgisGeometryToGeoJson(
          geometry as __esri.GeometryUnion
        );

        setPolygon(geoJson);
      }
    },
    [onDrawingUpdate, latestGeometryType]
  );

  const {
    drawingLayerRef: drawLayerRef,
    sketchViewModelRef,
    clearDrawingLayer,
    cancelActiveDrawing,
  } = useArcgisSketchLayer({
    mapView,
    layerTitle: 'Drawing Layer',
    onCreateComplete: handleCreateGeometry,
  });

  useEffect(() => {
    if (!mapView?.map) return;
    const map = mapView.map;
    const pendingLayer = pendingLayerRef.current;
    map.add(pendingLayer);

    return () => {
      map.remove(pendingLayer);
    };
  }, [mapView]);

  const stopDrawing = useCallback(() => {
    cancelActiveDrawing();
    clearDrawingLayer();
  }, [cancelActiveDrawing, clearDrawingLayer]);

  const setPolygonDrawingColor = useCallback((colorHex: string) => {
    setPolygonColorHex(colorHex);
  }, []);

  const setPolygonDrawingOpacity = useCallback((opacity?: number) => {
    setPolygonOpacity(normalizeOpacity(opacity));
  }, []);

  useEffect(() => {
    if (geometryType !== 'polygon') return;

    const polygonSymbol = createPolygonFillSymbol(
      polygonColorHex,
      polygonOpacity
    );
    if (sketchViewModelRef.current) {
      sketchViewModelRef.current.polygonSymbol = polygonSymbol;
    }

    drawLayerRef.current.graphics.forEach((graphic: Graphic) => {
      if (graphic.geometry?.type === 'polygon') {
        graphic.symbol = polygonSymbol;
      }
    });
  }, [
    drawLayerRef,
    geometryType,
    polygonColorHex,
    polygonOpacity,
    sketchViewModelRef,
  ]);

  const startDrawing = useCallback(
    (symbolKey: UscgSymbol) => {
      const type = symbolToGeometryTypeMap[symbolKey];
      const symbol = symbolToPictureMarkerMap[symbolKey];
      if (!sketchViewModelRef.current) return;

      const sketchViewModel = sketchViewModelRef.current;

      setGeometryType(type);
      setPoints([]);
      setLine(null);
      setPolygon(null);

      stopDrawing();

      // Reset create options on every symbol change so mode from a previous
      // tool (for example, oil spill freehand) does not leak into the next one.
      sketchViewModel.defaultCreateOptions = {
        ...sketchViewModel.defaultCreateOptions,
        mode: 'click',
      };

      if (symbolKey === 'oilSpill') {
        sketchViewModel.polylineSymbol = createPolylineSymbol('oilSpill');
        sketchViewModel.defaultCreateOptions = {
          ...sketchViewModel.defaultCreateOptions,
          mode: 'freehand',
        };
        sketchViewModel.create('polyline');
        return;
      }

      if (type === 'polygon') {
        sketchViewModel.polygonSymbol = createPolygonFillSymbol(
          polygonColorHex,
          polygonOpacity
        );
        sketchViewModel.create('polygon');
        return;
      }

      if (!symbol) return;

      sketchViewModel.pointSymbol = symbol;
      sketchViewModel.create('point');
    },
    [polygonColorHex, polygonOpacity, sketchViewModelRef, stopDrawing]
  );

  const resetDrawing = useCallback(() => {
    setGeometryType(null);
    setPoints([]);
    setLine(null);
    setPolygon(null);
    stopDrawing();
  }, [stopDrawing]);

  const addPendingPoint = useCallback(
    ({
      symbolKey,
      point,
      pendingGraphicId,
    }: {
      symbolKey: UscgSymbol;
      point: PointGeometry;
      pendingGraphicId: string;
    }) => {
      const pendingLayer = pendingLayerRef.current;
      const pictureMarkerSymbol = symbolToPictureMarkerMap[symbolKey];
      if (!pictureMarkerSymbol) return;

      const geometry = new Point({
        longitude: point.longitude,
        latitude: point.latitude,
        spatialReference: WGS84_SPATIAL_REFERENCE,
      });

      pendingLayer.add(
        new Graphic({
          geometry,
          symbol: pictureMarkerSymbol,
          attributes: { pendingGraphicId },
        })
      );
    },
    []
  );

  const addPendingPolyline = useCallback(
    ({
      symbolKey,
      points: nextPoints,
      pendingGraphicId,
    }: {
      symbolKey: UscgSymbol;
      points: PointGeometry[];
      pendingGraphicId: string;
    }) => {
      if (nextPoints.length < 2) return;
      pendingLayerRef.current.add(
        new Graphic({
          geometry: createPolylineFromPoints(nextPoints),
          symbol: createPolylineSymbol(symbolKey),
          attributes: { pendingGraphicId },
        })
      );
    },
    []
  );

  const addPendingPolygon = useCallback(
    ({
      geometry,
      colorHex,
      opacity,
      pendingGraphicId,
    }: {
      geometry: Geometry;
      colorHex?: string;
      opacity?: number;
      pendingGraphicId: string;
    }) => {
      if (geometry.type !== 'Polygon') return;

      pendingLayerRef.current.add(
        new Graphic({
          geometry: new Polygon({ rings: geometry.coordinates }),
          symbol: createPolygonFillSymbol(colorHex, opacity),
          attributes: { pendingGraphicId },
        })
      );
    },
    []
  );

  const clearPendingGraphics = useCallback(() => {
    pendingLayerRef.current.removeAll();
  }, []);

  const removePendingGraphicsByPendingGraphicId = useCallback(
    (pendingGraphicId: string) => {
      const toRemove: Graphic[] = [];
      pendingLayerRef.current.graphics.forEach((graphic: Graphic) => {
        const attrs = graphic.attributes as Record<string, unknown> | undefined;
        if (attrs?.pendingGraphicId === pendingGraphicId) {
          toRemove.push(graphic);
        }
      });
      for (const graphic of toRemove) {
        pendingLayerRef.current.remove(graphic);
      }
    },
    []
  );

  const connectDrawingPoints = useCallback(
    (symbolKey: UscgSymbol) => {
      if (!geometryType || points.length < 2) return;

      const sortedPoints = [...points].sort((a, b) => a.order - b.order);
      setLine({
        id: uuidv4(),
        points: sortedPoints,
      });

      if (geometryType === 'line') {
        drawLayerRef.current.add(
          new Graphic({
            geometry: createPolylineFromPoints(sortedPoints),
            symbol: createPolylineSymbol(symbolKey),
          })
        );
      }
    },
    [geometryType, points, drawLayerRef]
  );

  return {
    startDrawing,
    setPolygonDrawingColor,
    setPolygonDrawingOpacity,
    stopDrawing: resetDrawing,
    addPendingPoint,
    addPendingPolyline,
    addPendingPolygon,
    clearPendingGraphics,
    removePendingGraphicsByPendingGraphicId,
    symbolDrawingState: {
      geometryType,
      points,
      line,
      polygon,
    },
    connectDrawingPoints,
    resetDrawing,
  };
};

export type UscgSymbolDrawingReturnType = ReturnType<
  typeof useUscgSymbolDrawing
>;
